import { Link, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Menu, Tooltip } from 'antd';
import { DownOutlined, LogoutOutlined, ToolOutlined, UserOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useStoreActions, useStoreState } from '../../store';
import { useColumnWidth } from '../../hooks/useColumnWidth';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { LocalizedText } from '../../components/i18n/LocalizedText';
import { ShipiumTag } from '../../components/shared/ShipiumTag';
import { getAdminConsoleBaseUrl } from '../utils';
import logo from '~/assets/symbol.png';
import { useSession } from '~/hooks/useSession';

const { SubMenu } = Menu;

const useStyles = createUseStyles({
  account: {
    marginRight: '8px',
    verticalAlign: 'top',
    cursor: 'pointer',
    transition: 'all .3s',
  },
  avatar: {
    margin: '10px',
    background: palette.Teal.normal,
  },
  avatarAdmin: {
    margin: '10px',
    background: 'white',
  },
  hidden: {
    display: 'none',
  },
  partnerMenu: {
    '& .ant-dropdown-menu-submenu-title': {
      padding: '0px',
    },
  },
});

export const AccountDropDown = () => {
  const activeUser = useStoreState((state) => state.session.user);
  const partners = useStoreState((state) => state.session.user?.partners);
  const setActivePartner = useStoreActions((action) => action.session.setActivePartner);
  const history = useHistory();
  const columnWidth = useColumnWidth();
  const { hasShipiumEmail, isShipiumView, setPartnerView } = useSession();
  const classes = useStyles();

  const adminPortalUrl = getAdminConsoleBaseUrl();

  const handleActivePartnerClick = (partnerId: string) => {
    if (partnerId !== activeUser?.sessionPartnerId) {
      setActivePartner({ partnerId, discardPath: true });
    }
  };

  const partnerList = partners
    ? partners
        ?.sort((a, b) => {
          if (a.partnerName.toLowerCase() < b.partnerName.toLowerCase()) {
            return -1;
          }
          if (a.partnerName.toLowerCase() > b.partnerName.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map((partner) => {
          return (
            <Menu.Item key={partner.partnerId} onClick={() => handleActivePartnerClick(partner.partnerId)}>
              {partner.partnerName}
            </Menu.Item>
          );
        })
    : null;

  const items: MenuProps['items'] = [
    {
      key: 'center',
      icon: <UserOutlined />,
      label: (
        <Link to={`/team-management/my-account`}>
          <LocalizedText token="layout.header.accountDropdownTitle" />
        </Link>
      ),
    },
    ...(hasShipiumEmail && isShipiumView
      ? [
          {
            key: 'viewAsPartner',
            label: (
              <Tooltip
                placement="left"
                title={'You are logged in as a Shipium admin. Toggle this to see what a partner user would see.'}
              >
                <UserSwitchOutlined /> View as Partner
              </Tooltip>
            ),
          },
        ]
      : []),
    ...(hasShipiumEmail && !isShipiumView
      ? [
          {
            key: 'viewAsAdmin',
            label: (
              <Tooltip
                placement="left"
                title={
                  'You are currently viewing what a partner user would see. Your API permissions however remain the same.'
                }
              >
                <UserSwitchOutlined /> View as Shipium Admin
              </Tooltip>
            ),
          },
        ]
      : []),
    ...(hasShipiumEmail
      ? [
          {
            key: 'adminPortalLink',
            label: (
              <Tooltip
                placement="left"
                title={'This is an admin feature. Opens up the Admin Portal in a different window.'}
              >
                <a href={adminPortalUrl} target="_blank" rel="noreferrer">
                  <ToolOutlined /> Open Admin Portal
                </a>
              </Tooltip>
            ),
          },
        ]
      : []),
    ...(partnerList
      ? [
          {
            key: 'partnerSub',
            label: (
              <SubMenu className={classes.partnerMenu} title="Partner" key="partnerSub">
                {partnerList}
              </SubMenu>
            ),
          },
        ]
      : []),
    {
      key: 'logout',
      icon: (
        <>
          <LogoutOutlined style={{ marginRight: '5px' }} />
        </>
      ),
      label: <LocalizedText token="common.logout" />,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'viewAsPartner') {
      setPartnerView({ type: 'set' });
    } else if (key === 'viewAsAdmin') {
      setPartnerView({ type: 'unset' });
    } else if (key === 'logout') {
      history.push('/logout');
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Dropdown menu={menuProps}>
      <span className={classes.account}>
        {hasShipiumEmail && (
          <Avatar
            size="small"
            icon={<img src={logo} width="30" alt="avatar" />}
            alt="avatar"
            className={classes.avatarAdmin}
          />
        )}
        {!hasShipiumEmail && <Avatar size="small" icon={<UserOutlined />} alt="avatar" className={classes.avatar} />}
        <span className={classNames({ [classes.hidden]: columnWidth === 'sm' || columnWidth === 'xs' })}>
          {activeUser?.givenName} {activeUser?.surname}{' '}
          {hasShipiumEmail && !isShipiumView ? <ShipiumTag color={'Coral'}>partner view</ShipiumTag> : null}
        </span>
        <DownOutlined style={{ marginLeft: 5 }} />
      </span>
    </Dropdown>
  );
};
